/**
 * Utility functions for handling subdomain-based whitelabeling
 */

export interface SubdomainConfig {
  id?: string;
  subdomain: string;
  /** Which whitelabel template this tenant gets (see shared/config/orgTemplates.ts).
   *  Tenants created before this field existed resolve to "InstitutionAlumni". */
  institutionType?: string;
  /** Optional organization this subdomain serves (several orgs under one
   *  affiliation can each run a distinct whitelabel). Org-aware surfaces use
   *  organizationId || affiliationId (affiliation-as-tenant fallback). */
  organizationId?: string;
  organizationName?: string;
  brandName?: string; // Display name for branding (shown instead of affiliation name)
  tagline?: string;
  logoUrl?: string;
  logoFileName?: string; // Original filename for reference
  iconUrl?: string; // PWA icon URL (separate from logo)
  iconFileName?: string; // Original icon filename for reference
  primaryColor?: string;
  secondaryColor?: string;
  /** Optional theme tokens (page canvas / card surface / body text). */
  canvasColor?: string;
  surfaceColor?: string;
  textColor?: string;
  fontFamily?: string;
  /** Stylesheet URL for custom fonts (allowlisted CDNs only). */
  fontCssUrl?: string;
  customCss?: string;
  /** Alumni-institution tenants only: show the big/little Lineage section on profiles.
   *  Off by default; clubs never show it regardless. */
  showLineage?: boolean;
  favicon?: string;
  description?: string;
  isActive?: boolean;
  affiliationId: string; // Associated affiliation ID - users must be members to access (required)
  affiliationName?: string; // Cached affiliation name for display
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
  createdBy?: string; // Staff user ID
  updatedBy?: string; // Staff user ID
}

/**
 * Extracts subdomain from current hostname
 * @returns subdomain string or null if no subdomain or localhost
 */
export const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  // For whitelabel development, allow special handling
  // Check for development environment indicators
  const isDevelopment =
    hostname === "localhost" ||
    hostname.startsWith("localhost:") ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.");

  if (isDevelopment) {
    // In development, check for query parameter or environment variable for testing
    const urlParams = new URLSearchParams(window.location.search);
    const testSubdomain =
      urlParams.get("subdomain") ||
      (window as any).__TEST_SUBDOMAIN__ ||
      localStorage.getItem("dev-subdomain");

    if (testSubdomain) {
      return testSubdomain;
    }

    // For pure localhost without subdomain, return null
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.")
    ) {
      return null;
    }
  }

  // Split hostname and check for subdomain
  const parts = hostname.split(".");

  // Handle different hostname patterns
  // For localhost development: demo.localhost (2 parts)
  // For production: demo.zurilink.com (3+ parts)
  const isLocalhost = hostname.includes("localhost");
  const minParts = isLocalhost ? 2 : 3;

  if (parts.length < minParts) {
    return null;
  }

  // Return the first part as subdomain
  const subdomain = parts[0];

  // Skip common subdomains that aren't whitelabel
  const systemSubdomains = ["www", "api", "admin", "mail", "ftp", "localhost"];
  if (systemSubdomains.includes(subdomain)) {
    return null;
  }

  return subdomain;
};

/**
 * Checks if current page is accessed via subdomain
 */
export const isSubdomainAccess = (): boolean => {
  return getSubdomain() !== null;
};

/**
 * Gets full domain without subdomain
 */
export const getBaseDomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  if (parts.length < 2) {
    return hostname;
  }

  // Return last two parts (domain.com)
  return parts.slice(-2).join(".");
};

// Note: Default configurations are now handled in subdomain.service.ts

/**
 * Gets configuration for a specific subdomain
 * @param subdomain - The subdomain to get config for
 * @returns SubdomainConfig or null if not found
 */
export const getSubdomainConfig = async (
  subdomain: string
): Promise<SubdomainConfig | null> => {
  // Use the subdomain service for better caching and API management
  const { subdomainService } = await import("@/services/subdomain.service");
  return subdomainService.getSubdomainConfig(subdomain);
};

/**
 * Gets current subdomain configuration
 */
export const getCurrentSubdomainConfig =
  async (): Promise<SubdomainConfig | null> => {
    const subdomain = getSubdomain();
    if (!subdomain) {
      return null;
    }

    try {
      const config = await getSubdomainConfig(subdomain);

      // If online request fails, try to get cached version
      if (!config && "caches" in window) {
        try {
          const { getCachedSubdomainConfig } = await import(
            "@/services/pwaManifest.service"
          );
          return await getCachedSubdomainConfig(subdomain);
        } catch (cacheError) {
          console.error("Error getting cached subdomain config:", cacheError);
        }
      }

      return config;
    } catch (error) {
      console.error("Error getting current subdomain config:", error);

      // Try to get cached version as fallback
      if ("caches" in window) {
        try {
          const { getCachedSubdomainConfig } = await import(
            "@/services/pwaManifest.service"
          );
          return await getCachedSubdomainConfig(subdomain);
        } catch (cacheError) {
          console.error(
            "Error getting cached subdomain config as fallback:",
            cacheError
          );
        }
      }

      return null;
    }
  };

/**
 * Generates CSS custom properties from subdomain config
 */
export const generateCustomCSSProperties = (
  config: SubdomainConfig
): Record<string, string> => {
  const properties: Record<string, string> = {};

  // --brand-* is read directly by many components (Auth, Communities, legal pages).
  // --whitelabel-* is what index.css's base layer (body font/colors, cards, borders,
  // focus rings) actually reads — both must be set or theming silently no-ops.
  if (config.primaryColor) {
    properties["--brand-primary"] = config.primaryColor;
    properties["--whitelabel-primary"] = config.primaryColor;
  }

  if (config.secondaryColor) {
    properties["--brand-secondary"] = config.secondaryColor;
    properties["--whitelabel-secondary"] = config.secondaryColor;
  }

  if (config.canvasColor) {
    properties["--whitelabel-canvas"] = config.canvasColor;
  }

  if (config.surfaceColor) {
    properties["--whitelabel-surface"] = config.surfaceColor;
  }

  if (config.textColor) {
    properties["--whitelabel-text"] = config.textColor;
  }

  if (config.fontFamily) {
    properties["--whitelabel-font"] =
      `"${config.fontFamily}", ui-sans-serif, system-ui, sans-serif`;
  }

  return properties;
};

/**
 * Applies custom CSS properties to document root
 */
export const applyCustomBranding = (config: SubdomainConfig): void => {
  const properties = generateCustomCSSProperties(config);
  const root = document.documentElement;

  Object.entries(properties).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });

  // Update favicon if provided (check both favicon and iconUrl fields)
  const faviconUrl = config.favicon || config.iconUrl;
  if (faviconUrl) {
    // Update all favicon links
    const faviconLinks = document.querySelectorAll('link[rel="icon"]');
    faviconLinks.forEach((link) => {
      (link as HTMLLinkElement).href = faviconUrl;
    });
    
    // Also update apple-touch-icon links for iOS
    const appleTouchIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    appleTouchIcons.forEach((link) => {
      (link as HTMLLinkElement).href = faviconUrl;
    });
  }

  // Update document title
  const displayName = config.brandName || config.affiliationName;
  if (displayName) {
    document.title = `${displayName} - Powered by Zurilink`;
  }
};
